/**
 * Layout mention service for @ symbol layout input
 * Shows layout suggestions from state.json as user types @layout_name
 */

import { getApiBaseUrl } from '../utils/apiConfig';
import { filesystemAPI } from './api';

export interface LayoutMentionItem {
  id: string;
  title: string;
  referenceImagePath: string;
  generatedHtmlPath: string;
  description?: string;
  tags?: string[];
  createdAt?: string;
}

export interface LayoutMentionResult {
  items: LayoutMentionItem[];
  searchTerm: string;
  hasMore: boolean;
}

class LayoutMentionService {
  private layoutCache = new Map<string, LayoutMentionItem[]>();
  private cacheTimestamp = new Map<string, number>();
  private readonly CACHE_TTL = 15000; // 15 seconds cache (reduced for faster updates)

  /**
   * Parse @ command from input text
   */
  parseAtCommand(input: string): { prefix: string; command: string; suffix: string } | null {
    const cursorPos = input.length;
    
    // Find the last @ symbol before cursor
    let atPos = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      if (input[i] === '@') {
        // Check if this @ is at start of word (not preceded by alphanumeric)
        if (i === 0 || !/[\w]/.test(input[i - 1])) {
          atPos = i;
          break;
        }
      }
      // Stop at whitespace or newline
      if (/\s/.test(input[i])) {
        break;
      }
    }

    if (atPos === -1) return null;

    // Find end of command (next whitespace or end of string)
    let endPos = cursorPos;
    for (let i = atPos + 1; i < input.length; i++) {
      if (/\s/.test(input[i])) {
        endPos = i;
        break;
      }
    }

    const prefix = input.substring(0, atPos);
    const command = input.substring(atPos + 1, endPos);
    const suffix = input.substring(endPos);

    return { prefix, command, suffix };
  }

  /**
   * Load layouts from state.json for a conversation
   */
  private async loadLayouts(conversationId: string): Promise<LayoutMentionItem[]> {
    // Check cache first
    const cacheKey = conversationId;
    const cached = this.layoutCache.get(cacheKey);
    const cacheTime = this.cacheTimestamp.get(cacheKey);
    
    if (cached && cacheTime && Date.now() - cacheTime < this.CACHE_TTL) {
      return cached;
    }

    try {
      const stateResponse = await filesystemAPI.read('state.json', conversationId);
      const stateData = JSON.parse(stateResponse.content);
      
      const layouts: LayoutMentionItem[] = [];
      
      if (stateData.layouts && Array.isArray(stateData.layouts)) {
        layouts.push(...stateData.layouts);
      }
      
      // Cache the results
      this.layoutCache.set(cacheKey, layouts);
      this.cacheTimestamp.set(cacheKey, Date.now());
      
      return layouts;
    } catch (error) {
      console.warn('LayoutMentionService - Could not load state.json:', error);
      return [];
    }
  }

  /**
   * Get autocomplete suggestions for layout mentions
   */
  async getAutoCompleteSuggestions(
    input: string,
    options: { conversationId?: string; maxResults?: number } = {}
  ): Promise<LayoutMentionResult> {
    const { conversationId, maxResults = 10 } = options;

    if (!conversationId) {
      return { items: [], searchTerm: '', hasMore: false };
    }

    const parsed = this.parseAtCommand(input);
    if (!parsed) {
      return { items: [], searchTerm: '', hasMore: false };
    }

    const searchTerm = parsed.command.toLowerCase();

    try {
      const allLayouts = await this.loadLayouts(conversationId);
      
      // Filter layouts based on search term
      let filteredLayouts = allLayouts;
      
      if (searchTerm) {
        filteredLayouts = allLayouts.filter(layout => 
          layout.id.toLowerCase().includes(searchTerm) ||
          layout.title.toLowerCase().includes(searchTerm) ||
          (layout.tags && layout.tags.some(tag => 
            tag.toLowerCase().includes(searchTerm)
          ))
        );
      }

      // Sort by relevance (exact matches first, then starts with, then contains)
      filteredLayouts.sort((a, b) => {
        const aId = a.id.toLowerCase();
        const bId = b.id.toLowerCase();
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();

        // Exact matches first
        if (aId === searchTerm && bId !== searchTerm) return -1;
        if (bId === searchTerm && aId !== searchTerm) return 1;
        if (aTitle === searchTerm && bTitle !== searchTerm) return -1;
        if (bTitle === searchTerm && aTitle !== searchTerm) return 1;

        // Starts with matches next
        const aIdStarts = aId.startsWith(searchTerm);
        const bIdStarts = bId.startsWith(searchTerm);
        const aTitleStarts = aTitle.startsWith(searchTerm);
        const bTitleStarts = bTitle.startsWith(searchTerm);

        if ((aIdStarts || aTitleStarts) && !(bIdStarts || bTitleStarts)) return -1;
        if ((bIdStarts || bTitleStarts) && !(aIdStarts || aTitleStarts)) return 1;

        // Alphabetical for the rest
        return aTitle.localeCompare(bTitle);
      });

      const hasMore = filteredLayouts.length > maxResults;
      const items = filteredLayouts.slice(0, maxResults);
      
      return {
        items,
        searchTerm,
        hasMore
      };
    } catch (error) {
      console.error('LayoutMentionService - Error getting suggestions:', error);
      return { items: [], searchTerm, hasMore: false };
    }
  }

  /**
   * Get layout files for attachment (HTML + reference image)
   */
  async getLayoutFiles(
    layoutId: string,
    conversationId: string
  ): Promise<{ htmlFile?: File; imageFile?: File }> {
    try {
      const layouts = await this.loadLayouts(conversationId);
      const layout = layouts.find(l => l.id === layoutId);
      
      if (!layout) {
        console.warn(`LayoutMentionService - Layout not found: ${layoutId}`);
        return {};
      }

      const files: { htmlFile?: File; imageFile?: File } = {};

      // Load HTML file
      try {
        const htmlResponse = await filesystemAPI.read(layout.generatedHtmlPath, conversationId);
        const htmlBlob = new Blob([htmlResponse.content], { type: 'text/html' });
        files.htmlFile = new File([htmlBlob], `${layoutId}.html`, { type: 'text/html' });
      } catch (error) {
        console.warn(`LayoutMentionService - Could not load HTML file: ${layout.generatedHtmlPath}`, error);
      }

      // Load reference image
      try {
        // Construct image URL and fetch it
        const cleanPath = layout.referenceImagePath.startsWith('./') 
          ? layout.referenceImagePath.slice(2) 
          : layout.referenceImagePath;
        const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/');
        // API_BASE_URL is now dynamically retrieved via getApiBaseUrl()
        const baseUrl = getApiBaseUrl();
        const imageUrl = `${baseUrl}/filesystem/image/${encodedPath}?conversationId=${conversationId}`;
        
        const imageResponse = await fetch(imageUrl);
        if (imageResponse.ok) {
          const imageBlob = await imageResponse.blob();
          const filename = layout.referenceImagePath.split('/').pop() || 'reference.png';
          files.imageFile = new File([imageBlob], filename, { type: imageBlob.type });
        }
      } catch (error) {
        console.warn(`LayoutMentionService - Could not load reference image: ${layout.referenceImagePath}`, error);
      }

      return files;
    } catch (error) {
      console.error('LayoutMentionService - Error getting layout files:', error);
      return {};
    }
  }

  /**
   * Clear cache for a specific conversation
   */
  clearCache(conversationId?: string): void {
    if (conversationId) {
      this.layoutCache.delete(conversationId);
      this.cacheTimestamp.delete(conversationId);
    } else {
      this.layoutCache.clear();
      this.cacheTimestamp.clear();
    }
  }
}

export default new LayoutMentionService();